/**
 * The one dialog chrome (owner, 18 Oct 2026: "make them look really professional"; and on the first
 * attempt, "the pastel infills look old"). Every pop-over in the module — a figure's why, Explain
 * this, What could go wrong, the comparisons, the questions — wears this, so the reader meets one
 * surface, not five.
 *
 * WHAT IT IS NOT. No coloured chips, no caps eyebrow, no gradient bar, no tinted header wash, no
 * boxed cards inside: those are the template tells the owner rejected. The design is typographic —
 * a quiet one-line context above, a large navy title, one intro line, a hairline, then the content
 * set like a ledger (hairline rows, tabular figures, a heavier rule above the total). Colour is
 * reserved for meaning: navy for structure, rose and emerald only on a signed figure, amber only as
 * a thin rail beside what is about this client. Escape closes; the backdrop closes.
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export interface DialogShellProps {
    titleId: string;
    title: React.ReactNode;
    /** One quiet line above the title saying what this is about — the plan's name, the figure's owner. Sentence case. */
    eyebrow?: React.ReactNode;
    /** One line under the title saying how to read what follows. */
    intro?: React.ReactNode;
    /** A way back one step, shown in place of the context line. */
    back?: { label: string; onClick: () => void } | null;
    onClose: () => void;
    footer?: React.ReactNode;
    children: React.ReactNode;
    size?: 'md' | 'lg' | 'xl';
    testId?: string;
    closeRef?: React.Ref<HTMLButtonElement>;
    /** Above another dialog (the figure pop-over opens over the plans; the glossary over that). */
    zIndex?: 50 | 60 | 70;
    /** No body padding — for a page that is one table. */
    flush?: boolean;
}

const SIZE: Record<NonNullable<DialogShellProps['size']>, string> = { md: 'max-w-[680px]', lg: 'max-w-3xl', xl: 'max-w-5xl' };

/** A section label set like a ledger heading: small navy text with a hairline running to the edge. Use inside any dialog body. */
export const DialogSection = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
    <p className={`flex items-center gap-3 text-[12.5px] font-semibold text-brand-navy ${className}`}>
        <span className="shrink-0">{children}</span>
        <span className="h-px flex-1 bg-slate-200" aria-hidden />
    </p>
);

const DialogShell = ({ titleId, title, eyebrow, intro, back = null, onClose, footer, children, size = 'md', testId, closeRef, zIndex = 50, flush = false }: DialogShellProps) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    const z = zIndex === 70 ? 'z-[70]' : zIndex === 60 ? 'z-[60]' : 'z-50';
    return createPortal(
        <div className={`fixed inset-0 ${z} flex items-start justify-center overflow-y-auto bg-[#0F2A4A]/55 p-4 backdrop-blur-[3px] sm:p-8`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} role="presentation">
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                data-testid={testId}
                className={`my-auto flex w-full ${SIZE[size]} max-h-[calc(100vh-4rem)] flex-col overflow-hidden rounded-xl bg-white shadow-[0_32px_80px_-24px_rgba(0,30,122,0.45),0_2px_6px_rgba(15,42,74,0.08)]`}
            >
                <div className="relative shrink-0 border-b border-slate-200 px-8 pb-5 pt-7 pr-16">
                    {back ? (
                        <button type="button" onClick={back.onClick} className="mb-2 inline-flex items-center gap-1 text-[13.5px] font-semibold text-brand-navy hover:underline" data-testid="figure-back">‹ {back.label}</button>
                    ) : eyebrow ? (
                        <p className="mb-2 text-[13.5px] leading-snug text-slate-500">{eyebrow}</p>
                    ) : null}
                    <h2 id={titleId} className="text-[26px] font-bold leading-[1.15] tracking-[-0.01em] text-brand-navy">{title}</h2>
                    {intro && <p className="mt-2.5 max-w-[64ch] text-[15px] leading-relaxed text-slate-600">{intro}</p>}
                    <button type="button" ref={closeRef} onClick={onClose} aria-label="Close" className="absolute right-6 top-6 grid h-9 w-9 place-items-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-900">
                        <X size={20} strokeWidth={1.75} aria-hidden />
                    </button>
                </div>
                <div className={`min-h-0 flex-1 overflow-y-auto ${flush ? '' : 'px-8 py-6'}`}>{children}</div>
                {footer && <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-slate-200 px-8 py-4">{footer}</div>}
            </div>
        </div>,
        document.body,
    );
};

export default DialogShell;
